import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  LogOut,
  Users,
  Shield,
  Mail,
  UserPlus,
  UserMinus,
  Crown,
  Settings,
  Loader2,
  CheckCircle,
  AlertCircle,
  Building2,
  Globe,
  Plus,
  Trash2,
  Edit,
  FileText,
} from 'lucide-react';
import { useLocation } from 'wouter';
import { useToast } from '@/hooks/use-toast';

// Types
interface WhitelistedEmail {
  id: string;
  email: string;
  department: string | null;
  assignedRole: string;
  status: string;
  createdAt: string;
}

interface WhitelistedDomain {
  id: string;
  domain: string;
  companyName: string | null;
  isActive: boolean;
  autoAssignRole: string;
  allowSubdomains: boolean;
  createdAt: string;
}

interface AdminUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  department: string | null;
  createdAt: string;
  termsAcceptedAt?: string | null;
  termsVersion?: string | null;
  privacyAcceptedAt?: string | null;
  privacyVersion?: string | null;
}

// API functions
const fetchWhitelistedEmails = async (): Promise<WhitelistedEmail[]> => {
  const res = await fetch('/api/super-admin/whitelisted-emails', { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch whitelisted emails');
  return res.json();
};

const fetchWhitelistedDomains = async (): Promise<WhitelistedDomain[]> => {
  const res = await fetch('/api/super-admin/domains', { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch whitelisted domains');
  return res.json();
};

const fetchAdminUsers = async (): Promise<AdminUser[]> => {
  const res = await fetch('/api/super-admin/admin-users', { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch admin users');
  return res.json();
};

const fetchAllUsers = async (): Promise<AdminUser[]> => {
  const res = await fetch('/api/super-admin/all-users', { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to fetch users');
  return res.json();
};

const SuperAdminPanel = () => {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Form state for emails
  const [newEmail, setNewEmail] = useState('');
  const [newDepartment, setNewDepartment] = useState('');
  const [newRole, setNewRole] = useState('user');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [newUserRole, setNewUserRole] = useState('user');

  // Form state for domains
  const [newDomain, setNewDomain] = useState('');
  const [newDomainCompany, setNewDomainCompany] = useState('');
  const [newDomainRole, setNewDomainRole] = useState('user');
  const [newDomainAllowSubdomains, setNewDomainAllowSubdomains] = useState(false);
  const [showAddDomainDialog, setShowAddDomainDialog] = useState(false);
  const [editingDomain, setEditingDomain] = useState<WhitelistedDomain | null>(null);
  const [showEditDomainDialog, setShowEditDomainDialog] = useState(false);

  // Queries
  const { data: whitelistedEmails = [], isLoading: loadingEmails } = useQuery({
    queryKey: ['super-admin-whitelist'],
    queryFn: fetchWhitelistedEmails,
  });

  const { data: whitelistedDomains = [], isLoading: loadingDomains } = useQuery({
    queryKey: ['super-admin-domains'],
    queryFn: fetchWhitelistedDomains,
  });

  const { data: adminUsers = [], isLoading: loadingAdmins } = useQuery({
    queryKey: ['super-admin-admins'],
    queryFn: fetchAdminUsers,
  });

  const { data: allUsers = [], isLoading: loadingUsers } = useQuery({
    queryKey: ['super-admin-all-users'],
    queryFn: fetchAllUsers,
  });

  // Mutations for emails
  const addEmailMutation = useMutation({
    mutationFn: async (data: { email: string; department?: string; assignedRole: string }) => {
      const res = await apiRequest('POST', '/api/super-admin/whitelisted-emails', data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['super-admin-whitelist'] });
      toast({ title: 'Email whitelisted', description: 'Email has been added to whitelist.' });
      setNewEmail('');
      setNewDepartment('');
      setNewRole('user');
      setShowAddDialog(false);
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const deleteEmailMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest('DELETE', `/api/super-admin/whitelisted-emails/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['super-admin-whitelist'] });
      toast({ title: 'Email removed', description: 'Email removed from whitelist.' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to remove email', variant: 'destructive' });
    },
  });

  // Mutations for domains
  const addDomainMutation = useMutation({
    mutationFn: async (data: {
      domain: string;
      companyName?: string;
      autoAssignRole: string;
      allowSubdomains: boolean;
    }) => {
      const res = await apiRequest('POST', '/api/super-admin/domains', data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['super-admin-domains'] });
      toast({ title: 'Domain whitelisted', description: 'Domain has been added to whitelist.' });
      setNewDomain('');
      setNewDomainCompany('');
      setNewDomainRole('user');
      setNewDomainAllowSubdomains(false);
      setShowAddDomainDialog(false);
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const updateDomainMutation = useMutation({
    mutationFn: async (data: {
      id: string;
      companyName?: string;
      autoAssignRole?: string;
      allowSubdomains?: boolean;
      isActive?: boolean;
    }) => {
      const res = await apiRequest('PATCH', `/api/super-admin/domains/${data.id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['super-admin-domains'] });
      toast({ title: 'Domain updated', description: 'Domain settings have been updated.' });
      setShowEditDomainDialog(false);
      setEditingDomain(null);
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const deleteDomainMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest('DELETE', `/api/super-admin/domains/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['super-admin-domains'] });
      toast({ title: 'Domain removed', description: 'Domain removed from whitelist.' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to remove domain', variant: 'destructive' });
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: async (data: { userId: string; role: string }) => {
      const res = await apiRequest('PATCH', `/api/super-admin/users/${data.userId}/role`, {
        role: data.role,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['super-admin-admins'] });
      queryClient.invalidateQueries({ queryKey: ['super-admin-all-users'] });
      toast({ title: 'Role updated', description: 'User role has been updated.' });
      setShowRoleDialog(false);
      setSelectedUser(null);
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'super_admin':
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-purple-600 text-white">
            Super Admin
          </span>
        );
      case 'hr_admin':
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-blue-500 text-white">HR Admin</span>
        );
      case 'content_admin':
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-green-500 text-white">
            Content Admin
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-gray-200 text-gray-700">User</span>
        );
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'registered':
      case 'active':
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">
            Registered
          </span>
        );
      case 'pending':
      default:
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700">
            Pending
          </span>
        );
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-600 rounded-lg">
              <Crown className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Super Admin Panel</h1>
              <p className="text-muted-foreground">Manage users, roles, and access</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => setLocation('/logout')}
            className="text-red-600 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        <Tabs defaultValue="domains" className="space-y-6">
          <TabsList className="grid grid-cols-5 w-full bg-purple-100">
            <TabsTrigger value="domains" className="text-xs sm:text-sm">
              <Globe className="w-4 h-4 mr-1 hidden sm:inline" />
              Domains
            </TabsTrigger>
            <TabsTrigger value="whitelist" className="text-xs sm:text-sm">
              <Mail className="w-4 h-4 mr-1 hidden sm:inline" />
              Whitelist
            </TabsTrigger>
            <TabsTrigger value="admins" className="text-xs sm:text-sm">
              <Shield className="w-4 h-4 mr-1 hidden sm:inline" />
              Admins
            </TabsTrigger>
            <TabsTrigger value="users" className="text-xs sm:text-sm">
              <Users className="w-4 h-4 mr-1 hidden sm:inline" />
              All Users
            </TabsTrigger>
            <TabsTrigger value="consents" className="text-xs sm:text-sm">
              <FileText className="w-4 h-4 mr-1 hidden sm:inline" />
              T&amp;C
            </TabsTrigger>
          </TabsList>

          {/* Domains Tab */}
          <TabsContent value="domains" className="space-y-4">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-foreground flex items-center gap-2">
                  <Globe className="w-5 h-5 text-purple-600" />
                  Whitelisted Domains
                  {loadingDomains && <Loader2 className="w-4 h-4 animate-spin" />}
                </h3>
                <Dialog open={showAddDomainDialog} onOpenChange={setShowAddDomainDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-purple-600 hover:bg-purple-700">
                      <Plus className="w-4 h-4 mr-1" />
                      Add Domain
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Whitelist New Domain</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div>
                        <Label>Domain *</Label>
                        <Input
                          type="text"
                          value={newDomain}
                          onChange={(e) => setNewDomain(e.target.value)}
                          placeholder="company.com"
                          className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Enter the email domain (e.g., company.com)
                        </p>
                      </div>
                      <div>
                        <Label>Company Name (optional)</Label>
                        <Input
                          value={newDomainCompany}
                          onChange={(e) => setNewDomainCompany(e.target.value)}
                          placeholder="Company Inc."
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Auto-Assign Role</Label>
                        <Select value={newDomainRole} onValueChange={setNewDomainRole}>
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">User</SelectItem>
                            <SelectItem value="hr_admin">HR Admin</SelectItem>
                            <SelectItem value="content_admin">Content Admin</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">
                          Users from this domain will automatically receive this role
                        </p>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Allow Subdomains</Label>
                          <p className="text-xs text-muted-foreground">
                            Allow registrations from *.company.com
                          </p>
                        </div>
                        <Switch
                          checked={newDomainAllowSubdomains}
                          onCheckedChange={setNewDomainAllowSubdomains}
                        />
                      </div>
                      <Button
                        onClick={() =>
                          addDomainMutation.mutate({
                            domain: newDomain,
                            companyName: newDomainCompany || undefined,
                            autoAssignRole: newDomainRole,
                            allowSubdomains: newDomainAllowSubdomains,
                          })
                        }
                        className="w-full bg-purple-600 hover:bg-purple-700"
                        disabled={!newDomain || addDomainMutation.isPending}
                      >
                        {addDomainMutation.isPending && (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        )}
                        Add Domain
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              <ScrollArea className="h-80">
                <div className="space-y-3">
                  {whitelistedDomains.map((domain) => (
                    <div
                      key={domain.id}
                      className={`flex items-center justify-between p-4 rounded-xl ${
                        domain.isActive ? 'bg-slate-50' : 'bg-slate-100 opacity-60'
                      }`}
                    >
                      <div className="flex-1">
                        <div className="font-medium text-foreground text-sm flex items-center gap-2">
                          @{domain.domain}
                          {domain.allowSubdomains && (
                            <span className="text-xs text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full">
                              +subdomains
                            </span>
                          )}
                          {!domain.isActive && (
                            <span className="text-xs text-red-600 bg-red-100 px-2 py-0.5 rounded-full">
                              Inactive
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground flex items-center gap-2 mt-1">
                          {domain.companyName && (
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {domain.companyName}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getRoleBadge(domain.autoAssignRole)}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-blue-500 hover:text-blue-600 hover:bg-blue-50"
                          onClick={() => {
                            setEditingDomain(domain);
                            setShowEditDomainDialog(true);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => deleteDomainMutation.mutate(domain.id)}
                          disabled={deleteDomainMutation.isPending}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {whitelistedDomains.length === 0 && !loadingDomains && (
                    <div className="text-center text-muted-foreground py-8">
                      <Globe className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      No whitelisted domains yet
                    </div>
                  )}
                </div>
              </ScrollArea>
            </Card>

            <Card className="p-4 bg-purple-50 border-purple-200">
              <p className="text-sm text-purple-800">
                <strong>Domain Whitelist:</strong> Only users with email addresses from whitelisted
                domains can register. Enable subdomains to allow registrations from any subdomain
                (e.g., user@dept.company.com).
              </p>
            </Card>
          </TabsContent>

          {/* Whitelist Tab */}
          <TabsContent value="whitelist" className="space-y-4">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-foreground flex items-center gap-2">
                  <Mail className="w-5 h-5 text-purple-600" />
                  Whitelisted Emails
                  {loadingEmails && <Loader2 className="w-4 h-4 animate-spin" />}
                </h3>
                <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-purple-600 hover:bg-purple-700">
                      <UserPlus className="w-4 h-4 mr-1" />
                      Add Email
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Whitelist New Email</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div>
                        <Label>Email Address</Label>
                        <Input
                          type="email"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          placeholder="user@company.com"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Department (optional)</Label>
                        <Input
                          value={newDepartment}
                          onChange={(e) => setNewDepartment(e.target.value)}
                          placeholder="Engineering"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Assigned Role</Label>
                        <Select value={newRole} onValueChange={setNewRole}>
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">User</SelectItem>
                            <SelectItem value="hr_admin">HR Admin</SelectItem>
                            <SelectItem value="content_admin">Content Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        onClick={() =>
                          addEmailMutation.mutate({
                            email: newEmail,
                            department: newDepartment || undefined,
                            assignedRole: newRole,
                          })
                        }
                        className="w-full bg-purple-600 hover:bg-purple-700"
                        disabled={!newEmail || addEmailMutation.isPending}
                      >
                        {addEmailMutation.isPending && (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        )}
                        Add to Whitelist
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              <ScrollArea className="h-80">
                <div className="space-y-3">
                  {whitelistedEmails.map((entry) => (
                    <div
                      key={entry.id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                    >
                      <div className="flex-1">
                        <div className="font-medium text-foreground text-sm">{entry.email}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-2 mt-1">
                          {entry.department && (
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {entry.department}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getRoleBadge(entry.assignedRole)}
                        {getStatusBadge(entry.status)}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => deleteEmailMutation.mutate(entry.id)}
                          disabled={deleteEmailMutation.isPending}
                        >
                          <UserMinus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {whitelistedEmails.length === 0 && !loadingEmails && (
                    <div className="text-center text-muted-foreground py-8">
                      <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      No whitelisted emails yet
                    </div>
                  )}
                </div>
              </ScrollArea>
            </Card>

            <Card className="p-4 bg-purple-50 border-purple-200">
              <p className="text-sm text-purple-800">
                <strong>Note:</strong> Only whitelisted emails can sign up. When you assign a role
                here, the user will receive that role upon registration.
              </p>
            </Card>
          </TabsContent>

          {/* Admins Tab */}
          <TabsContent value="admins" className="space-y-4">
            <Card className="p-6">
              <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-600" />
                Current Admins
                {loadingAdmins && <Loader2 className="w-4 h-4 animate-spin" />}
              </h3>

              <ScrollArea className="h-80">
                <div className="space-y-3">
                  {adminUsers.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-slate-50 rounded-xl"
                    >
                      <div>
                        <div className="font-medium text-foreground">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-xs text-muted-foreground">{user.email}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getRoleBadge(user.role)}
                        {user.role !== 'super_admin' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedUser(user);
                              setNewUserRole(user.role);
                              setShowRoleDialog(true);
                            }}
                          >
                            <Settings className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {adminUsers.length === 0 && !loadingAdmins && (
                    <div className="text-center text-muted-foreground py-8">
                      No admin users found
                    </div>
                  )}
                </div>
              </ScrollArea>
            </Card>
          </TabsContent>

          {/* All Users Tab */}
          <TabsContent value="users" className="space-y-4">
            <Card className="p-6">
              <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-600" />
                All Registered Users ({allUsers.length})
                {loadingUsers && <Loader2 className="w-4 h-4 animate-spin" />}
              </h3>

              <ScrollArea className="h-96">
                <div className="space-y-2">
                  {allUsers.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors gap-2 overflow-hidden"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-foreground text-sm truncate">
                          {user.firstName || user.email} {user.lastName || ''}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {user.email} {user.department && `• ${user.department}`}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {getRoleBadge(user.role)}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedUser(user);
                            setNewUserRole(user.role);
                            setShowRoleDialog(true);
                          }}
                          disabled={user.role === 'super_admin'}
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {allUsers.length === 0 && !loadingUsers && (
                    <div className="text-center text-muted-foreground py-8">No users found</div>
                  )}
                </div>
              </ScrollArea>
            </Card>
          </TabsContent>

          {/* T&C Acceptance Tracking */}
          <TabsContent value="consents" className="space-y-4">
            <Card className="p-6 overflow-hidden">
              <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                Terms &amp; Privacy Acceptance
                {loadingUsers && <Loader2 className="w-4 h-4 animate-spin" />}
              </h3>
              <p className="text-xs text-muted-foreground mb-4">
                Audit trail for Terms &amp; Conditions and Privacy Policy acceptance: user id,
                email, document versions, and timestamps recorded via{' '}
                <code className="text-[11px]">POST /api/auth/accept-terms</code> (also available at{' '}
                <code className="text-[11px]">GET /api/super-admin/consents</code>).
              </p>
              <ScrollArea className="h-96">
                <div className="space-y-2">
                  {allUsers.map((user) => {
                    const accepted = Boolean(user.termsAcceptedAt && user.privacyAcceptedAt);
                    return (
                      <div
                        key={user.id}
                        className="flex items-start justify-between p-3 bg-slate-50 rounded-lg gap-3 overflow-hidden"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm text-foreground truncate">
                            {user.firstName || ''} {user.lastName || ''}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                          <div className="text-[11px] text-muted-foreground font-mono break-all mt-0.5">
                            User ID: {user.id}
                          </div>
                          {accepted ? (
                            <div className="text-[11px] text-muted-foreground mt-1 break-words">
                              T&amp;C {user.termsVersion || '—'} ·{' '}
                              {user.termsAcceptedAt
                                ? new Date(user.termsAcceptedAt).toLocaleString()
                                : '—'}
                              <br />
                              Privacy {user.privacyVersion || '—'} ·{' '}
                              {user.privacyAcceptedAt
                                ? new Date(user.privacyAcceptedAt).toLocaleString()
                                : '—'}
                            </div>
                          ) : (
                            <div className="text-[11px] text-amber-700 mt-1">
                              No acceptance timestamps recorded yet.
                            </div>
                          )}
                        </div>
                        <span
                          className={`text-xs font-semibold px-2 py-1 rounded-full shrink-0 ${
                            accepted ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {accepted ? 'Accepted' : 'Pending'}
                        </span>
                      </div>
                    );
                  })}
                  {allUsers.length === 0 && !loadingUsers && (
                    <div className="text-center text-muted-foreground py-8">No users found</div>
                  )}
                </div>
              </ScrollArea>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Role Change Dialog */}
        <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Change User Role</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <Label>User</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedUser?.firstName} {selectedUser?.lastName} ({selectedUser?.email})
                </p>
              </div>
              <div>
                <Label>New Role</Label>
                <Select value={newUserRole} onValueChange={setNewUserRole}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="hr_admin">HR Admin</SelectItem>
                    <SelectItem value="content_admin">Content Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={() =>
                  selectedUser &&
                  updateRoleMutation.mutate({
                    userId: selectedUser.id,
                    role: newUserRole,
                  })
                }
                className="w-full bg-purple-600 hover:bg-purple-700"
                disabled={updateRoleMutation.isPending}
              >
                {updateRoleMutation.isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Update Role
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Domain Dialog */}
        <Dialog open={showEditDomainDialog} onOpenChange={setShowEditDomainDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Domain Settings</DialogTitle>
            </DialogHeader>
            {editingDomain && (
              <div className="space-y-4 pt-4">
                <div>
                  <Label>Domain</Label>
                  <p className="text-sm font-medium text-foreground mt-1">
                    @{editingDomain.domain}
                  </p>
                </div>
                <div>
                  <Label>Company Name</Label>
                  <Input
                    value={editingDomain.companyName || ''}
                    onChange={(e) =>
                      setEditingDomain({ ...editingDomain, companyName: e.target.value || null })
                    }
                    placeholder="Company Inc."
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Auto-Assign Role</Label>
                  <Select
                    value={editingDomain.autoAssignRole}
                    onValueChange={(value) =>
                      setEditingDomain({ ...editingDomain, autoAssignRole: value })
                    }
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="hr_admin">HR Admin</SelectItem>
                      <SelectItem value="content_admin">Content Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Allow Subdomains</Label>
                    <p className="text-xs text-muted-foreground">
                      Allow registrations from *.{editingDomain.domain}
                    </p>
                  </div>
                  <Switch
                    checked={editingDomain.allowSubdomains}
                    onCheckedChange={(checked) =>
                      setEditingDomain({ ...editingDomain, allowSubdomains: checked })
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Active</Label>
                    <p className="text-xs text-muted-foreground">
                      Allow registrations from this domain
                    </p>
                  </div>
                  <Switch
                    checked={editingDomain.isActive}
                    onCheckedChange={(checked) =>
                      setEditingDomain({ ...editingDomain, isActive: checked })
                    }
                  />
                </div>
                <Button
                  onClick={() =>
                    updateDomainMutation.mutate({
                      id: editingDomain.id,
                      companyName: editingDomain.companyName || undefined,
                      autoAssignRole: editingDomain.autoAssignRole,
                      allowSubdomains: editingDomain.allowSubdomains,
                      isActive: editingDomain.isActive,
                    })
                  }
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  disabled={updateDomainMutation.isPending}
                >
                  {updateDomainMutation.isPending && (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  )}
                  Save Changes
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default SuperAdminPanel;
